/**
 * Shared style constants for the Commander Control Room modal family
 * (FormModal, ConfirmDialog, ConfirmDestructive, FeedbackOverlay).
 *
 * The visual language — 2px coloured top rail, eyebrow micro-label in
 * JetBrains Mono uppercase, sharp corners (--radius-sm), hairline action bar
 * — was signed off as part of the 2026-05-27 redesign. These constants
 * exist purely to dedupe the inline-style blocks each modal used to carry
 * verbatim; do not tweak them in isolation.
 */

import { type CSSProperties } from 'react';

/** Backdrop opacity for the modal family. Sharp, not soft. */
export const MODAL_OVERLAY_OPACITY = 0.72;

/** Mantine transition timing used across the modal family. */
export const MODAL_TRANSITION = { duration: 120 } as const;

/**
 * Mantine `styles` block for the modal content surface. Pass the rail colour
 * (a CSS variable reference) and you get the 2px top rail + sharp corners
 * + hairline border the modal family shares.
 */
export function modalContentStyles(rail: string) {
  return {
    content: {
      background: 'var(--bg-card)',
      borderTop: `2px solid ${rail}`,
      borderRadius: 'var(--radius-sm, 4px)',
      border: '1px solid var(--border-colour)',
      borderTopWidth: 2,
      borderTopColor: rail,
      boxShadow: 'none',
    },
    body: { padding: 0, background: 'var(--bg-card)' },
    header: { display: 'none' },
  } as const;
}

/** Padding for the header block (eyebrow + title + optional description). */
export const headerBoxStyle: CSSProperties = {
  padding: '22px 26px 18px',
};

/**
 * Eyebrow micro-label — JetBrains Mono uppercase, 11px, 0.16em tracking.
 * Pass a colour to tint it (e.g. error accent on destructive eyebrows).
 */
export function eyebrowStyle(color: string = 'var(--text-muted)', marginBottom: number = 8): CSSProperties {
  return {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    letterSpacing: '0.16em',
    textTransform: 'uppercase',
    color,
    marginBottom,
    fontWeight: 500,
  };
}

/** Modal title — Geist semibold, tight tracking. Size varies per modal. */
export function titleStyle(fontSize: number): CSSProperties {
  return {
    fontFamily: 'var(--font-sans)',
    fontSize,
    lineHeight: fontSize >= 20 ? 1.25 : 1.3,
    fontWeight: 600,
    letterSpacing: '-0.01em',
    color: 'var(--text-primary)',
  };
}

/** Hairline action bar — 1px top separator, bg-base footer band. */
export const actionBarStyle: CSSProperties = {
  padding: '14px 26px',
  borderTop: '1px solid var(--border-colour)',
  background: 'var(--bg-base)',
};

/**
 * Left-accented callout panel (used for error banners in FormModal and the
 * impact summary in ConfirmDestructive). Pass the accent CSS variable name
 * stem (e.g. 'error' or 'warning').
 */
export function calloutStyle(family: 'error' | 'warning'): CSSProperties {
  return {
    padding: '10px 14px',
    borderRadius: 'var(--radius-sm, 4px)',
    background: `var(--status-${family}-bg)`,
    borderLeft: `3px solid var(--status-${family}-accent)`,
  };
}

/** Mono-uppercase label inside a callout (e.g. "ERROR", "IMPACT"). */
export function calloutLabelStyle(family: 'error' | 'warning'): CSSProperties {
  return {
    fontFamily: 'var(--font-mono)',
    fontSize: 11,
    letterSpacing: '0.14em',
    textTransform: 'uppercase',
    color: `var(--status-${family}-accent)`,
    fontWeight: 500,
    marginRight: 8,
  };
}
